import * as Device from "@-/com.npmjs.expo-device";
import onetime from "@-/com.npmjs.onetime";
import { Platform } from "@-/com.npmjs.react-native";
import { LaunchArguments } from "@-/com.npmjs.react-native-launch-arguments";

/**
 * How much drawing this device's GPU takes in the app's showpiece animations (the welcome galaxy,
 * the onboarding globe): `standard`, or `low` for a phone whose GPU would drop frames under them,
 * which then draws fewer particles, skips the finest lines and steps its animations at no more
 * than 60 frames a second.
 */
export type GraphicsTier = "low" | "standard";

/** What the tier is decided from: the platform, and what `expo-device` knows of the phone. */
export type GraphicsTierDevice = {
  /** A `hanGraphicsTier` launch argument, which forces a tier (QA, E2E). */
  override: unknown;
  platform: string;
  /** Total RAM (bytes), where known. */
  totalMemory: number | null;
  /** The year a phone of this class was the flagship (`Device.deviceYearClass`), where known. */
  yearClass: number | null;
};

/** Android phones with less RAM than this (bytes) are low-end, whatever their year. */
const LOW_ANDROID_MEMORY = 4 * 1024 ** 3;
/** Android phones whose class is older than this year are low-end. */
const LOW_ANDROID_YEAR_CLASS = 2019;
/** iPhones older than this (the A11's iPhone 8 and X, which iOS 16 still runs) are low-end. */
const LOW_IOS_YEAR_CLASS = 2018;

/**
 * The tier for `device`. Anything unknown (a simulator, the web) is `standard`: a tier only drops
 * detail where the phone is known to be weak.
 */
export function decideGraphicsTier({
  override,
  platform,
  totalMemory,
  yearClass,
}: GraphicsTierDevice): GraphicsTier {
  if (override === "low" || override === "standard") {
    return override;
  }
  if (platform === "android") {
    const isLowMemory = totalMemory !== null && totalMemory < LOW_ANDROID_MEMORY;
    const isOld = yearClass !== null && yearClass < LOW_ANDROID_YEAR_CLASS;
    return isLowMemory || isOld ? "low" : "standard";
  }
  if (platform === "ios") {
    return yearClass !== null && yearClass < LOW_IOS_YEAR_CLASS ? "low" : "standard";
  }
  return "standard";
}

function readLaunchArgumentOverride(): unknown {
  try {
    return LaunchArguments.value<{ hanGraphicsTier?: unknown }>().hanGraphicsTier;
  } catch {
    // Reading launch arguments throws where the native module is absent (web, vitest).
    return undefined;
  }
}

/** This device's tier, decided once per launch (nothing it reads can change while the app runs). */
export const getGraphicsTier = onetime((): GraphicsTier =>
  decideGraphicsTier({
    override: readLaunchArgumentOverride(),
    platform: Platform.OS,
    totalMemory: Device.totalMemory ?? null,
    yearClass: Device.deviceYearClass ?? null,
  }),
);
